<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <div class="login-container">
    <div class="login-wrapper">
      <div class="login-content">
        <div class="login-card">
          <div class="login-header">
            <div id="loginLogo"></div>
            <div class="login-title">
              {{ $t("welcome") }}
            </div>
            <span class="login-subtitle">{{ $t("connectToContinue") }}</span>
          </div>
          <PrimeMessage v-if="loginError" severity="error" :closable="false">{{
            failLoginMessage
          }}</PrimeMessage>
          <div class="login-form">
            <label for="email" class="form-label">{{
              $t("userIdentification")
            }}</label>
            <PrimeInputText
              id="email"
              v-model="email"
              type="text"
              class="form-input"
              :placeholder="$t('userID')"
              fluid
              @keyup.enter="login"
            />

            <label for="password" class="form-label">{{
              $t("password")
            }}</label>
            <PrimePassword
              id="password"
              v-model="password"
              :placeholder="$t('password')"
              class="form-input"
              :feedback="false"
              fluid
              @keyup.enter="login"
            />
            <div class="forgot-password-wrapper">
              <a class="forgot-password-link" @click="goToForgotPasswordPage">{{
                $t("forgotPasswordLink")
              }}</a>
            </div>
            <PrimeButton
              :label="$t('connect')"
              class="login-button"
              @click="login"
            />

            <template v-if="SHOW_ACA_LOGIN_LINK">
              <PrimeDivider />
              <PrimeButton
                :label="$t(`accessACA`)"
                class="aca-button"
                severity="secondary"
                @click="goToACA"
              />
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { useConfigStore } from "@/stores/config";
import { useUserStore } from "@/stores/user";

import { mapState } from "pinia";
import Button from "primevue/button";
import Message from "primevue/message";
import InputText from "primevue/inputtext";
import Password from "primevue/password";
import Divider from "primevue/divider";

export default {
  components: {
    PrimeButton: Button,
    PrimeMessage: Message,
    PrimeDivider: Divider,
    PrimeInputText: InputText,
    PrimePassword: Password,
  },
  data() {
    return {
      email: "",
      password: "",
      checked: false,
      loginError: false,
      failLoginMessage: null,
    };
  },
  methods: {
    goToForgotPasswordPage() {
      this.$router.push({ name: "forgot-password" });
    },
    goToACA() {
      window.location.href = this.ACA_LINK;
    },
    login() {
      const user = useUserStore();
      user
        .login(this.email, this.password)
        .then((data) => {
          if (data === false) {
            throw new Error(this.$t("login.emptyPasswordOrUser"));
          }
          if (this.$route.query?.target === undefined) {
            this.$router.push({ name: "welcome" });
          } else {
            this.$router.replace(this.$route.query.target);
          }
        })
        .catch((err) => {
          this.loginError = true;
          if (err instanceof Error) {
            this.failLoginMessage = err.message;
          } else if (err && typeof err === "object" && err.message) {
            this.failLoginMessage = err.message;
          } else if (typeof err === "string") {
            this.failLoginMessage = err;
          } else {
            this.failLoginMessage = this.$t("login.unexpectedError");
          }
        });
    },
  },
  computed: {
    ...mapState(useConfigStore, ["SHOW_ACA_LOGIN_LINK", "ACA_LINK"]),
  },
};
</script>
<style scoped>
.login-container {
  background-color: var(--p-surface-ground);
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  min-width: 100vw;
  overflow: hidden;
}

.login-wrapper {
  display: flex;
  justify-content: center;
  padding: 0.5rem;
  min-width: 80%;
}

.login-content {
  width: 100%;
}

.login-card {
  height: 100%;
  width: 100%;
  margin: 0;
  padding: 3.5rem 1rem;
  border-radius: 53px;
  background: var(--p-surface-100);
}

.app-dark .login-card {
  background: var(--p-surface-800);
}

.login-header {
  text-align: center;
  margin-bottom: 2rem;
}

#loginLogo {
  background-position: center;
  background-size: contain;
  width: 14rem;
  height: 14rem;
  background-repeat: no-repeat;
  background-image: url("@/assets/logo/logo-pristy-square.svg");
  display: inline-block;
  margin-bottom: 1rem;
}

.login-title {
  color: var(--p-text-color);
  font-size: 1.875rem;
  font-weight: 500;
  margin-bottom: 1rem;
}

.login-subtitle {
  color: var(--p-text-muted-color);
  font-weight: 500;
}

.login-form {
  width: 100%;
  max-width: 25rem;
  margin: 0 auto;
}

.form-label {
  display: block;
  color: var(--p-text-color);
  font-size: 1.25rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
}

.form-input {
  width: 100%;
  margin-bottom: 1rem;
}

.forgot-password-wrapper {
  width: 100%;
  margin-bottom: 1rem;
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
}

.forgot-password-link {
  cursor: pointer;
  text-decoration: none;
  color: var(--p-primary-color);
}

.forgot-password-link:hover {
  text-decoration: underline;
}

.login-button {
  width: 100%;
  font-size: 1.25rem;
}

.aca-button {
  width: 100%;
  font-size: 1.25rem;
}

@media (min-width: 1200px) {
  .login-content {
    width: 50%;
  }
}

@media (min-width: 992px) {
  .login-wrapper {
    padding: 0;
  }
}
</style>
